// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

export interface PagedResponse<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PagedResponseDto<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Entity Types
export interface Company {
  id: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  website?: string;
  logoUrl?: string;
  accentColor?: string;
  createdDate: string;
  isActive: boolean;
  companyType?: number;
  // First Page Template (company-wide)
  firstPageTemplateName?: string;
  firstPageTemplateContent?: string;
  firstPageTemplateActive?: boolean;
  firstPageTemplateUpdatedDate?: string;
  // Session settings
  sessionExpiryMinutes?: number | null;
  sessionWarningLeadSeconds?: number | null;
  currencyCode?: string;
  // Sequence tracking
  lastInvoiceNumber?: number;
  lastWorkOrderNumber?: number;
}

export interface Role {
  id: number;
  name: string;
  description?: string;
  userCount?: number;
}

export interface CreateRoleForm {
  name: string;
  description?: string;
}

export interface UpdateRoleForm {
  name: string;
  description?: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  companyId: number;
  roleId: number;
  roleName?: string;
  createdDate: string;
  isActive: boolean;
  forcePasswordChange: boolean;
  profilePhotoUrl?: string;
  company?: Company;
  role?: Role;
}

export interface Client {
  id: number;
  companyId: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  rank?: number;
  createdAt: string;
  isActive: boolean;
  
  // Location information
  primaryLocationId?: number;
  primaryLocationName?: string;
  primaryLocationAddress?: string;
  primaryLocationCity?: string;
  primaryLocationState?: string;
  primaryLocationZipCode?: string;
  locationCount: number;
}

export interface Category {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateCategoryForm {
  name: string;
  description?: string;
  isActive: boolean;
}

export interface UpdateCategoryForm {
  name: string;
  description?: string;
  isActive: boolean;
}

export interface Vendor {
  id: number;
  companyId: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  website?: string;
  notes?: string;
  rank?: number;
  rating?: number;
  latitude?: number;
  longitude?: number;
  createdDate: string;
  isActive: boolean;
  distance?: number;
  categories: Category[];
  complianceStatus?: VendorComplianceStatus;
  complianceStatusLabel?: string;
  w9OnFile?: boolean;
}

export enum VendorComplianceStatus {
  Compliant = 1,
  ExpiringSoon = 2,
  NonCompliant = 3
}

export enum InsurancePolicyType {
  GeneralLiability = 1,
  Auto = 2,
  Umbrella = 3,
  WorkersCompensation = 4,
  Other = 5,
  Property = 6
}

export enum ComplianceReviewStatus {
  NotOnFile = 0,
  PendingReview = 1,
  Accepted = 2,
  Rejected = 3
}

export enum VendorComplianceDocumentType {
  CodeOfConduct = 1,
  SafetyAttestation = 2
}

export interface VendorComplianceIssue {
  code: string;
  message: string;
  severity: 'Warning' | 'Blocking';
  policyId?: number;
  policyType?: InsurancePolicyType;
  policyTypeLabel?: string;
  expirationDate?: string;
  daysUntilExpiration?: number;
}

export interface VendorComplianceRequirementItem {
  key: string;
  category: string;
  label: string;
  status: 'Compliant' | 'Alert' | 'NeedsReview' | 'Missing' | 'Rejected' | string;
  statusLabel: string;
  detail?: string;
  relevantDate?: string;
}

export interface CompanyComplianceSummary {
  totalVendors: number;
  compliantCount: number;
  expiringSoonCount: number;
  nonCompliantCount: number;
  pendingReviewCount: number;
  needsAttentionCount: number;
}

export interface VendorComplianceAttention {
  vendorId: number;
  vendorName: string;
  complianceStatus: VendorComplianceStatus;
  complianceStatusLabel: string;
  hasPendingReview: boolean;
  primaryIssue?: string;
}

export interface VendorInsurancePolicy {
  id: number;
  vendorId: number;
  policyType: InsurancePolicyType;
  policyTypeLabel: string;
  customPolicyType?: string;
  carrierName?: string;
  policyNumber?: string;
  coverageLimit?: number;
  effectiveDate?: string;
  expirationDate?: string;
  documentPath?: string;
  isVerified: boolean;
  verifiedBy?: string;
  verifiedDate?: string;
  notes?: string;
  policyStatus: string;
  daysUntilExpiration?: number;
  createdDate: string;
  updatedDate?: string;
}

export interface VendorCompliance {
  vendorId: number;
  vendorName: string;
  complianceStatus: VendorComplianceStatus;
  complianceStatusLabel: string;
  w9OnFile: boolean;
  w9ReviewStatus: ComplianceReviewStatus;
  w9ReviewStatusLabel: string;
  w9ReceivedDate?: string;
  w9DocumentPath?: string;
  w9RejectionReason?: string;
  w9ReviewedDate?: string;
  requiresAcknowledgment: boolean;
  issues: VendorComplianceIssue[];
  requirements?: VendorComplianceRequirementItem[];
  policies: VendorInsurancePolicy[];
  documents: VendorComplianceDocument[];
}

export interface VendorComplianceDocument {
  id: number;
  vendorId: number;
  documentType: VendorComplianceDocumentType;
  documentTypeLabel: string;
  documentPath?: string;
  reviewStatus: ComplianceReviewStatus;
  reviewStatusLabel: string;
  receivedDate?: string;
  reviewedBy?: string;
  reviewedDate?: string;
  rejectionReason?: string;
}

export interface CompanyComplianceSettings {
  companyId: number;
  requireGeneralLiability: boolean;
  requireAuto: boolean;
  requireWorkersCompensation: boolean;
  requireUmbrella: boolean;
  requireProperty: boolean;
  minGeneralLiabilityLimit?: number;
  minAutoLimit?: number;
  minWorkersCompensationLimit?: number;
  minUmbrellaLimit?: number;
  minPropertyLimit?: number;
  requirePolicyVerification: boolean;
  requireW9Review: boolean;
  requireCodeOfConduct: boolean;
  requireSafetyAttestation: boolean;
  createdDate: string;
  updatedDate?: string;
}

export interface UpdateCompanyComplianceSettingsForm {
  requireGeneralLiability: boolean;
  requireAuto: boolean;
  requireWorkersCompensation: boolean;
  requireUmbrella: boolean;
  requireProperty: boolean;
  minGeneralLiabilityLimit?: number;
  minAutoLimit?: number;
  minWorkersCompensationLimit?: number;
  minUmbrellaLimit?: number;
  minPropertyLimit?: number;
  requirePolicyVerification: boolean;
  requireW9Review: boolean;
  requireCodeOfConduct: boolean;
  requireSafetyAttestation: boolean;
}

export interface CompanyNotificationSettings {
  companyId: number;
  recipientUserIds: string[];
  additionalEmails: string[];
  emailOnWorkOrderCompleted: boolean;
  inAppOnWorkOrderCompleted: boolean;
  emailOnReadyToInvoice: boolean;
  inAppOnReadyToInvoice: boolean;
  emailOnNteExceeded: boolean;
  inAppOnNteExceeded: boolean;
  emailOnClientApprovalSent: boolean;
  inAppOnClientApprovalSent: boolean;
  emailOnClientApproved: boolean;
  inAppOnClientApproved: boolean;
  emailOnClientRejected: boolean;
  inAppOnClientRejected: boolean;
  emailOnWorkOrderCreatedFromEmail: boolean;
  inAppOnWorkOrderCreatedFromEmail: boolean;
  emailOnEmailImportNeedsReview: boolean;
  inAppOnEmailImportNeedsReview: boolean;
  emailOnEmailImportFailed: boolean;
  inAppOnEmailImportFailed: boolean;
  createdDate: string;
  updatedDate?: string;
}

export interface UpdateCompanyNotificationSettingsForm {
  recipientUserIds: string[];
  additionalEmails: string[];
  emailOnWorkOrderCompleted: boolean;
  inAppOnWorkOrderCompleted: boolean;
  emailOnReadyToInvoice: boolean;
  inAppOnReadyToInvoice: boolean;
  emailOnNteExceeded: boolean;
  inAppOnNteExceeded: boolean;
  emailOnClientApprovalSent: boolean;
  inAppOnClientApprovalSent: boolean;
  emailOnClientApproved: boolean;
  inAppOnClientApproved: boolean;
  emailOnClientRejected: boolean;
  inAppOnClientRejected: boolean;
  emailOnWorkOrderCreatedFromEmail: boolean;
  inAppOnWorkOrderCreatedFromEmail: boolean;
  emailOnEmailImportNeedsReview: boolean;
  inAppOnEmailImportNeedsReview: boolean;
  emailOnEmailImportFailed: boolean;
  inAppOnEmailImportFailed: boolean;
}

export interface ReviewComplianceDocumentForm {
  accept: boolean;
  rejectionReason?: string;
}

export interface CreateVendorInsurancePolicyForm {
  policyType: InsurancePolicyType;
  customPolicyType?: string;
  carrierName?: string;
  policyNumber?: string;
  coverageLimit?: number;
  effectiveDate?: string;
  expirationDate?: string;
  notes?: string;
}

export interface UpdateVendorInsurancePolicyForm extends CreateVendorInsurancePolicyForm {
  isVerified: boolean;
}

export interface WorkOrderVendorAssignmentResult {
  assignment?: WorkOrderVendor;
  complianceWarning?: VendorCompliance;
  requiresComplianceAcknowledgment: boolean;
}

export interface WorkOrderDocumentationIssue {
  code: string;
  message: string;
  severity: string;
}

export interface WorkOrderDocumentationChecklist {
  photosComplete: boolean;
  coiComplete: boolean;
  vendorInvoiceComplete: boolean;
  w9Complete: boolean;
  isComplete: boolean;
  blockingIssueCount: number;
  primaryVendorId?: number;
  primaryVendorName?: string;
  issues: WorkOrderDocumentationIssue[];
}

export interface WorkOrderStatusUpdateResult {
  updated: boolean;
  requiresDocumentationAcknowledgment: boolean;
  documentationWarning?: WorkOrderDocumentationChecklist;
}

export interface CreateVendorForm {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email?: string;
  website?: string;
  notes?: string;
  rank?: number;
  rating?: number;
  latitude?: number;
  longitude?: number;
  isActive: boolean;
  categoryIds: number[];
}

export interface UpdateVendorForm {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email?: string;
  website?: string;
  notes?: string;
  rank?: number;
  rating?: number;
  latitude?: number;
  longitude?: number;
  isActive: boolean;
  categoryIds: number[];
}


export interface WorkOrderVendor {
  id: number;
  workOrderId: number;
  vendorId: number;
  vendorName: string;
  vendorPhone?: string;
  vendorAddress?: string;
  vendorCity?: string;
  vendorState?: string;
  vendorZipCode?: string;
  vendorEmail?: string;
  isPrimary: boolean;
  assignedDate: string;
  status: 'Assigned' | 'InProgress' | 'Completed' | 'Cancelled';
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  complianceStatus?: VendorComplianceStatus;
  complianceStatusLabel?: string;
}

export interface CreateWorkOrderVendor {
  vendorId: number;
  isPrimary: boolean;
  status: 'Assigned' | 'InProgress' | 'Completed' | 'Cancelled';
  notes?: string;
  acknowledgeComplianceWarning?: boolean;
  complianceOverrideReason?: string;
}

export interface UpdateWorkOrderVendor {
  isPrimary: boolean;
  status: 'Assigned' | 'InProgress' | 'Completed' | 'Cancelled';
  notes?: string;
}

export interface WorkOrder {
  id: number;
  companyId: number;
  clientId?: number;
  assignedUserId?: string;
  externalWorkOrderNumber?: string;
  
  priorityId: number;
  priorityName: string;
  statusId: number;
  statusName: string;
  statusThemeKey?: string;
  statusBadgeClass?: string;
  categoryId?: number;
  categoryName?: string;
  dueDate?: string;
  templateId?: number;
  description?: string;
  signedOff: boolean;
  invoiced: boolean;
  nteAmount?: number | null;
  incurredCost?: number | null;
  incurredCostNote?: string | null;
  proposedCost?: number | null;
  actualCost?: number | null;
  quoteRequested: boolean;
  pricingReceived: boolean;
  photosReceived: boolean;
  invoiceReceived: boolean;
  vendorPaid: boolean;
  vendorPaidDate?: string | null;
  vendorPaymentReference?: string | null;
  documentationOverrideReason?: string;
  documentationOverrideBy?: string;
  documentationOverrideDate?: string;
  documentationChecklist?: WorkOrderDocumentationChecklist;
  createdDate: string;
  createdBy: string;
  updatedDate?: string;
  client?: Client;
  vendors: WorkOrderVendor[];
  primaryVendor?: WorkOrderVendor;
  assignedUser?: User;
  createdByUser?: User;
  template?: WorkOrderTemplate;
}

export interface JobContactEntry {
  id: number;
  workOrderId: number;
  userId: string;
  userName?: string;
  userProfilePhotoUrl?: string;
  entryTypeId: number;
  entryTypeName: string;
  message: string;
  metadata?: string; // JSON field for entry type specific data
  createdDate: string;
  updatedDate?: string;
  user?: User;
  attachments?: JobContactAttachment[];
  
  // Billing tracking
  invoiceIds?: number[];
  invoiceNumbers?: string[]; // Actual invoice numbers for display
  isBilled?: boolean;
}

export interface JobContactAttachment {
  id: number;
  jobContactEntryId: number;
  fileName: string;
  fileSize: number;
  fileType: string;
  filePath: string;
  uploadedBy: string;
  uploadedDate: string;
  user?: User;
}

export interface UpdateJobContactEntryDto {
  message: string;
  metadata?: string;
  vendorId?: number;
  editReason: string;
  attachments?: File[];
}

export interface DeleteAttachmentDto {
  deleteReason: string;
}

export interface JobContactEntryHistoryDto {
  id: number;
  jobContactEntryId: number;
  previousMessage: string;
  previousMetadata?: string;
  newMessage: string;
  newMetadata?: string;
  editReason: string;
  editedBy: string;
  editedByName: string;
  editedDate: string;
  ipAddress?: string;
}

// Form Types
export interface LoginForm {
  email: string;
  password: string;
}

export interface RegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  companyName: string;
}

export interface CreateWorkOrderForm {
  clientId?: number;
  clientLocationId?: number;
  vendors: CreateWorkOrderVendor[];
  assignedUserId?: string;
  externalWorkOrderNumber?: string;
  
  priorityId: number;
  statusId: number;
  dueDate?: string;
  templateId?: number;
  description?: string;
}

export interface UpdateWorkOrderForm {
  clientId?: number;
  clientLocationId?: number;
  assignedUserId?: string;
  externalWorkOrderNumber?: string;
  
  priorityId: number;
  statusId: number;
  dueDate?: string;
  templateId?: number;
  description?: string;
}

export interface CreateClientForm {
  name: string;
  phone?: string;
  email?: string;
  rank?: number;
}

export interface UpdateClientForm {
  name: string;
  phone?: string;
  email?: string;
  rank?: number;
}



export interface CreateUserForm {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  roleId: number;
  sendWelcomeEmail?: boolean;
  forcePasswordChange?: boolean;
  isActive?: boolean;
}

export interface EntryType {
  entryTypeId: number;
  name: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
  createdDate: string;
}

export interface CreateEntryTypeDto {
  name: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
}

export interface UpdateEntryTypeDto {
  name: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
}

export interface CreateJobContactEntryForm {
  entryTypeId: number;
  message: string;
  vendorId?: number;
  metadata?: string; // JSON string for entry-specific data
  attachments?: File[];
}

// Filter Types
export interface WorkOrderFilters {
  status?: string;
  statusId?: number;
  priority?: string;
  priorityId?: number;
  assignedUserId?: string;
  clientId?: number;
  vendorId?: number;
  dueDateFrom?: string;
  dueDateTo?: string;
  search?: string;
  tabFilter?: string;
}

export interface ClientFilters {
  search?: string;
  isActive?: boolean;
}

export interface VendorFilters {
  search?: string;
  zipCode?: string;
  rating?: number;
  isActive?: boolean;
}

// Dashboard Types
export interface DashboardStats {
  totalWorkOrders: number;
  openWorkOrders: number;
  completedWorkOrders: number;
  totalClients: number;
  totalVendors: number;
  overdueWorkOrders: number;
}

export interface WorkOrderStatusChart {
  status: string;
  count: number;
}

export interface WorkOrderPriorityChart {
  priority: string;
  count: number;
}

export interface MonthlyWorkOrders {
  month: string;
  count: number;
}

// Navigation Types
export interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  current?: boolean;
  children?: NavigationItem[];
}

// Table Types
export interface Column<T> {
  id: string;
  header: string;
  accessorKey: keyof T;
  cell?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  sticky?: 'left' | 'right';
  stickyWidth?: string;
}

// Wizard Types
export interface WizardStep {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType<any>;
}

export interface WorkOrderWizardData {
  // Step 1: Client Selection
  selectedClient?: Client;
  selectedClientLocationId?: number;
  selectedClientLocationName?: string;
  selectedClientLocationCity?: string;
  selectedClientLocationState?: string;
  selectedClientLocationZipCode?: string;
  zipcode?: string;
  radius?: number;
  selectedCategories?: number[];
  
  // Step 2: Vendor Selection
  selectedVendors: CreateWorkOrderVendor[];
  
  // Step 3: Work Order Details
  assignedUserId?: string;
  priorityId?: number;
  statusId?: number;
  categoryId?: number;
  equipmentId?: number;
  unitId?: number;
  type?: WorkOrderType;
  dueDate?: string;
  externalWorkOrderNumber?: string;
  
  nteAmount?: number;
  description?: string;
  templateId?: number;
  fromEmailImport?: boolean;
}

export interface StepClientProps {
  data: WorkOrderWizardData;
  onChange: (data: Partial<WorkOrderWizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSubmitting: boolean;
  onWorkOrderCreated?: () => void; // Optional prop for step components
}

export interface StepVendorProps {
  data: WorkOrderWizardData;
  onChange: (data: Partial<WorkOrderWizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSubmitting: boolean;
  onWorkOrderCreated?: () => void; // Optional prop for step components
}

// Modal Types
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

// File Upload Types
export interface FileUploadProps {
  onUpload: (files: File[]) => void;
  multiple?: boolean;
  accept?: string;
  maxSize?: number;
  disabled?: boolean;
}

// Searchable Dropdown Types
export interface SearchableDropdownOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SearchableDropdownProps {
  options: SearchableDropdownOption[];
  value?: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  error?: string;
  autoComplete?: string;
}

// Auth Types
export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface AuthContextType extends AuthState {
  login: (credentials: LoginForm) => Promise<void>;
  logout: () => void;
  register: (data: RegisterForm) => Promise<void>;
  updateUserProfilePhoto: (profilePhotoUrl: string) => void;
  // Impersonation
  isImpersonating: boolean;
  impersonatedUser: User | null;
  startImpersonation: (token: string, impersonatedUser: User) => void;
  endImpersonation: () => Promise<void>;
}

export interface CompanyUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface VendorSearchDto {
  id: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  website?: string;
  notes?: string;
  rank?: number;
  rating?: number;
  isActive: boolean;
  distance: number; // Distance in miles
  categories: string[];
  complianceStatus?: VendorComplianceStatus;
  complianceStatusLabel?: string;
}

export interface StepDetailsProps {
  data: WorkOrderWizardData;
  onChange: (data: Partial<WorkOrderWizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSubmitting: boolean;
  onWorkOrderCreated?: () => void; // Optional prop for step components
}

// Work Order DTOs (matching backend)
export interface WorkOrderDto {
  id: number;
  workOrderNumber: string;
  workOrderNumberInt?: number;
  workOrderNumberFormatted: string;
  companyId: number;
  clientId: number;
  clientLocationId?: number;
  clientName: string;
  clientLocationName?: string;
  clientLocationContactName?: string;
  clientLocationContactPhone?: string;
  clientLocationContactEmail?: string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientZipCode?: string;
  clientPhone?: string;
  clientEmail?: string;
  vendors: WorkOrderVendor[];
  primaryVendor?: WorkOrderVendor;
  assignedUserId: string;
  assignedUserName: string;
  priorityId: number;
  priorityName: string;
  priorityColorCode: string;
  statusId: number;
  statusName: string;
  categoryId?: number;
  categoryName?: string;
  dueDate?: string;
  externalWorkOrderNumber?: string;
  description?: string;
  signedOff: boolean;
  invoiced: boolean;
  quoteRequested: boolean;
  pricingReceived: boolean;
  photosReceived: boolean;
  invoiceReceived: boolean;
  vendorPaid: boolean;
  vendorPaidDate?: string | null;
  vendorPaymentReference?: string | null;
  documentationOverrideReason?: string;
  documentationOverrideBy?: string;
  documentationOverrideDate?: string;
  documentationChecklist?: WorkOrderDocumentationChecklist;
  createdDate: string;
  createdBy?: string;
  updatedDate?: string;
  updatedBy?: string;
  templateId?: number;
  templateName?: string;
  equipmentId?: number;
  equipmentName?: string;
  type: number; // WorkOrderType enum: 0=Regular, 1=Preventative, 2=Emergency, 3=Project
  typeDisplay: string;
}

// Equipment Types
export enum EquipmentType {
  HVAC = 0,
  Refrigeration = 1,
  Plumbing = 2,
  Electrical = 3,
  FireSafety = 4,
  Kitchen = 5,
  Lighting = 6,
  BuildingSystems = 7,
  Other = 99
}

export enum EquipmentStatus {
  Active = 0,
  Retired = 1,
  UnderWarranty = 2
}

export enum UnitStatus {
  Occupied = 0,
  Vacant = 1,
  VacantNeedsWork = 2,
  UnderRenovation = 3,
  OffMarket = 4
}

export enum WorkOrderType {
  Regular = 0,
  Preventative = 1,
  Emergency = 2,
  Project = 3
}

export interface EquipmentPhotoDto {
  id: number;
  equipmentId: number;
  fileName: string;
  fileSize: number;
  fileType: string;
  filePath: string;
  uploadedBy: string;
  uploadedDate: string;
  description?: string;
}

export interface EquipmentDto {
  id: number;
  companyId: number;
  clientLocationId: number;
  clientLocationName: string;
  clientId: number;
  clientName: string;
  unitId?: number;
  equipmentType: EquipmentType;
  equipmentTypeDisplay: string;
  equipmentTypeIcon: string;
  customEquipmentType?: string;
  name: string;
  make?: string;
  model?: string;
  serialNumber?: string;
  installDate?: string;
  expectedLifespanYears?: number;
  replacementCost?: number;
  status: EquipmentStatus;
  statusDisplay: string;
  warrantyExpirationDate?: string;
  warrantyProvider?: string;
  notes?: string;
  photoUrl?: string; // Legacy field - kept for backward compatibility
  photos: EquipmentPhotoDto[];
  manualUrl?: string;
  createdDate: string;
  createdBy?: string;
  updatedDate?: string;
  updatedBy?: string;
  workOrderCount: number;
  totalMaintenanceCost: number;
  lastServiceDate?: string;
}

export interface CreateEquipmentDto {
  clientLocationId: number;
  unitId?: number;
  equipmentType: EquipmentType;
  customEquipmentType?: string;
  name: string;
  make?: string;
  model?: string;
  serialNumber?: string;
  installDate?: string;
  expectedLifespanYears?: number;
  replacementCost?: number;
  status: EquipmentStatus;
  warrantyExpirationDate?: string;
  warrantyProvider?: string;
  notes?: string;
  photoUrl?: string;
  manualUrl?: string;
}

export interface UpdateEquipmentDto {
  clientLocationId: number;
  unitId?: number;
  equipmentType: EquipmentType;
  customEquipmentType?: string;
  name: string;
  make?: string;
  model?: string;
  serialNumber?: string;
  installDate?: string;
  expectedLifespanYears?: number;
  replacementCost?: number;
  status: EquipmentStatus;
  warrantyExpirationDate?: string;
  warrantyProvider?: string;
  notes?: string;
  photoUrl?: string;
  manualUrl?: string;
}

export interface UnitDto {
  unitId: number;
  clientLocationId: number;
  clientLocationName: string;
  clientId: number;
  clientName: string;
  companyId: number;
  unitNumber: string;
  unitType?: string;
  squareFeet?: number;
  bedrooms?: number;
  bathrooms?: number;
  floor?: number;
  status: UnitStatus;
  statusDisplay: string;
  currentTenantId?: number;
  occupiedSince?: string;
  vacantSince?: string;
  monthlyRent?: number;
  securityDeposit?: number;
  accessNotes?: string;
  features?: string;
  createdAt: string;
  updatedAt?: string;
  equipmentCount: number;
  workOrderCount: number;
  openWorkOrderCount: number;
}

export interface CreateUnitDto {
  clientLocationId: number;
  unitNumber: string;
  unitType?: string;
  squareFeet?: number;
  bedrooms?: number;
  bathrooms?: number;
  floor?: number;
  status: UnitStatus;
  occupiedSince?: string;
  vacantSince?: string;
  monthlyRent?: number;
  securityDeposit?: number;
  accessNotes?: string;
  features?: string;
}

export interface UpdateUnitDto {
  clientLocationId: number;
  unitNumber: string;
  unitType?: string;
  squareFeet?: number;
  bedrooms?: number;
  bathrooms?: number;
  floor?: number;
  status: UnitStatus;
  occupiedSince?: string;
  vacantSince?: string;
  monthlyRent?: number;
  securityDeposit?: number;
  accessNotes?: string;
  features?: string;
}

export interface RepairVsReplaceAnalysisDto {
  equipmentId: number;
  equipmentName: string;
  totalMaintenanceCost: number;
  replacementCost: number;
  costPercentage: number;
  costStatus: 'good' | 'monitor' | 'replace';
  ageYears: number;
  expectedLifespanYears?: number;
  agePercentage: number;
  workOrderCount: number;
  lastServiceDate?: string;
  daysSinceLastService?: number;
  averageDaysBetweenServices?: number;
  last12MonthsCost: number;
  previous12MonthsCost: number;
  costTrend: 'increasing' | 'stable' | 'decreasing';
  recommendation: 'repair' | 'monitor' | 'replace';
  recommendationReason: string;
}

export interface EquipmentTypeTemplateDto {
  id: number;
  name: string;
  equipmentType: EquipmentType;
  category?: string;
  applicableToFacilities: boolean;
  applicableToRetail: boolean;
  applicableToHospitality: boolean;
  applicableToHealthcare: boolean;
  applicableToEducation: boolean;
  applicableToManufacturing: boolean;
  applicableToGovernment: boolean;
  applicableToChurches: boolean;
  expectedLifespanYears?: number;
  typicalReplacementCostMin?: number;
  typicalReplacementCostMax?: number;
  suggestedPMFrequencyMonths?: number;
  suggestedPMDescription?: string;
  icon?: string;
  displayOrder: number;
  isActive: boolean;
}

export interface CompanyEquipmentTypeTemplateDto {
  id: number;
  companyId: number;
  templateId: number;
  template?: EquipmentTypeTemplateDto;
  expectedLifespanYears?: number;
  typicalReplacementCostMin?: number;
  typicalReplacementCostMax?: number;
  suggestedPMFrequencyMonths?: number;
  isActive: boolean;
  createdDate: string;
}

export interface UpdateCompanyEquipmentTypeTemplateDto {
  expectedLifespanYears?: number;
  typicalReplacementCostMin?: number;
  typicalReplacementCostMax?: number;
  suggestedPMFrequencyMonths?: number;
  isActive: boolean;
}

// PM Schedule Types
export enum ScheduleType {
  Calendar = 0,
  MeterBased = 1
}

export enum FrequencyUnit {
  Days = 0,
  Weeks = 1,
  Months = 2,
  Years = 3
}

export enum ScheduledMaintenanceStatus {
  Scheduled = 0,
  WorkOrderGenerated = 1,
  Completed = 2,
  Skipped = 3
}

export interface MaintenanceScheduleDto {
  id: number;
  companyId: number;
  equipmentId: number;
  equipmentName: string;
  equipmentTypeDisplay: string;
  clientLocationName: string;
  clientName: string;
  name: string;
  description?: string;
  scheduleType: ScheduleType;
  frequencyValue: number;
  frequencyUnit: FrequencyUnit;
  frequencyDisplay: string;
  meterThreshold?: number;
  currentMeterValue?: number;
  nextDueDate: string;
  lastCompletedDate?: string;
  isOverdue: boolean;
  daysUntilDue: number;
  autoGenerateWorkOrders: boolean;
  workOrderTemplateId?: number;
  workOrderTemplateName?: string;
  assignedUserId?: string;
  assignedUserName?: string;
  defaultPriorityId?: number;
  defaultPriorityName?: string;
  defaultStatusId?: number;
  defaultStatusName?: string;
  leadTimeDays: number;
  isActive: boolean;
  createdDate: string;
  createdBy?: string;
  updatedDate?: string;
  updatedBy?: string;
  totalCompleted: number;
  totalSkipped: number;
  totalPending: number;
  lastWorkOrderGeneratedDate?: string;
}

export interface CreateMaintenanceScheduleDto {
  equipmentId: number;
  name: string;
  description?: string;
  scheduleType: ScheduleType;
  frequencyValue: number;
  frequencyUnit: FrequencyUnit;
  meterThreshold?: number;
  currentMeterValue?: number;
  nextDueDate: string;
  autoGenerateWorkOrders: boolean;
  workOrderTemplateId?: number;
  assignedUserId?: string;
  defaultPriorityId?: number;
  defaultStatusId?: number;
  leadTimeDays: number;
  isActive: boolean;
}

export interface UpdateMaintenanceScheduleDto {
  name?: string;
  description?: string;
  scheduleType?: ScheduleType;
  frequencyValue?: number;
  frequencyUnit?: FrequencyUnit;
  meterThreshold?: number;
  currentMeterValue?: number;
  nextDueDate?: string;
  lastCompletedDate?: string;
  autoGenerateWorkOrders?: boolean;
  workOrderTemplateId?: number;
  assignedUserId?: string;
  defaultPriorityId?: number;
  defaultStatusId?: number;
  leadTimeDays?: number;
  isActive?: boolean;
}

export interface ScheduledMaintenanceHistoryDto {
  id: number;
  maintenanceScheduleId: number;
  maintenanceScheduleName: string;
  workOrderId?: number;
  workOrderNumber?: string;
  generatedDate: string;
  scheduledDueDate: string;
  completedDate?: string;
  status: ScheduledMaintenanceStatus;
  statusDisplay: string;
  notes?: string;
}

export interface PMCheckResultDto {
  schedulesChecked: number;
  workOrdersGenerated: number;
  generatedWorkOrders: PMWorkOrderGeneratedDto[];
}

export interface PMWorkOrderGeneratedDto {
  workOrderId: number;
  workOrderNumber: string;
  equipmentId: number;
  equipmentName: string;
  maintenanceScheduleId: number;
  maintenanceScheduleName: string;
}

export interface ClientLocationDto {
  id: number;
  clientId: number;
  companyId: number;
  locationName: string;
  locationType: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  latitude?: number;
  longitude?: number;
  isPrimary: boolean;
  isActive: boolean;
  canReceiveWorkOrders: boolean;
  specialInstructions?: string;
  taxRate?: number | null;
  createdDate: string;
  updatedDate?: string;
}

export interface PriorityDto {
  id: number;
  name: string;
  code?: string;
  description?: string;
  colorCode: string;
  sortOrder: number;
  isActive: boolean;
}

export interface StatusDto {
  id: number;
  companyId: number;
  name: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  createdDate: string;
  updatedDate: string;
  tabGroup?: string;
}

export interface CreateStatusDto {
  name: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  tabGroup?: string;
}

export interface UpdateStatusDto {
  name: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  tabGroup?: string;
}

export interface CreateWorkOrderDto {
  clientId: number;
  clientLocationId?: number;
  // Note: unitId is not supported by backend - removed to prevent 400 errors
  vendors: CreateWorkOrderVendor[];
  assignedUserId?: string | null;
  priorityId: number;
  statusId: number; // Changed from status: string to match backend
  categoryId?: number;
  equipmentId?: number;
  type: WorkOrderType;
  dueDate?: string;
  templateId?: number;
  externalWorkOrderNumber?: string;
  description?: string;
  signedOff?: boolean;
  invoiced?: boolean;
  nteAmount?: number;
  incurredCost?: number | null;
  incurredCostNote?: string | null;
  proposedCost?: number | null;
  actualCost?: number | null;
  quoteRequested?: boolean;
  pricingReceived?: boolean;
  photosReceived?: boolean;
  invoiceReceived?: boolean;
  vendorPaid?: boolean;
  vendorPaidDate?: string | null;
  vendorPaymentReference?: string | null;
  acknowledgeDocumentationWarning?: boolean;
  documentationOverrideReason?: string;
}

export interface WorkOrderEmailImportPreview {
  parserUsed: string;
  overallConfidence: string;
  warnings: string[];
  extractedRaw: WorkOrderEmailImportExtracted;
  suggestedCreateWorkOrder: WorkOrderEmailImportSuggested;
  clientSuggestions: WorkOrderEmailImportClientSuggestion[];
  locationSuggestions: WorkOrderEmailImportLocationSuggestion[];
  categorySuggestions: WorkOrderEmailImportCategorySuggestion[];
  duplicateExternalWorkOrder?: WorkOrderEmailImportDuplicate;
}

export interface WorkOrderEmailImportExtracted {
  externalWorkOrderNumber?: string;
  customerName?: string;
  propertyName?: string;
  requestedBy?: string;
  forHeaderName?: string;
  siteAddress?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  problemText?: string;
  noteText?: string;
  description?: string;
  nteAmount?: number;
  dueDate?: string;
  emailPriority?: string;
  emailType?: string;
}

export interface WorkOrderEmailImportSuggested {
  clientId?: number;
  clientLocationId?: number;
  assignedUserId?: string | null;
  priorityId?: number;
  priorityName?: string;
  statusId?: number;
  statusName?: string;
  categoryId?: number;
  categoryName?: string;
  type?: WorkOrderType;
  dueDate?: string;
  externalWorkOrderNumber?: string;
  nteAmount?: number;
  description?: string;
}

export interface WorkOrderEmailImportClientSuggestion {
  clientId: number;
  clientName: string;
  matchScore: number;
  matchedOn: string;
}

export interface WorkOrderEmailImportLocationSuggestion {
  clientLocationId: number;
  locationName: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  matchScore: number;
}

export interface WorkOrderEmailImportCategorySuggestion {
  categoryId: number;
  categoryName: string;
  matchScore: number;
  matchedOn: string;
}

export interface WorkOrderEmailImportDuplicate {
  workOrderId: number;
  workOrderNumber: string;
  externalWorkOrderNumber: string;
}

export interface CompanyEmailIntegration {
  companyId: number;
  setupMethod: string;
  connectedEmail?: string;
  isGmailConnected: boolean;
  isEnabled: boolean;
  autoCreateEnabled: boolean;
  allowedSenderDomains: string[];
  knownSenderSystems: string[];
  lastEmailReceivedAt?: string;
  gmailConnectedAt?: string;
  lastPollAt?: string;
  lastPollError?: string;
  setupCompletedAt?: string;
  isConfigured: boolean;
  gmailOAuthConfigured: boolean;
  gmailPollingEnabled: boolean;
}

export interface UpdateCompanyEmailIntegrationForm {
  autoCreateEnabled?: boolean;
  isEnabled?: boolean;
  allowedSenderDomains?: string[];
  knownSenderSystems?: string[];
}

export interface InboundEmailLogEntry {
  id: number;
  companyId: number;
  gmailMessageId: string;
  subject?: string;
  fromAddress?: string;
  toAddress?: string;
  receivedAt: string;
  processedAt: string;
  status: string;
  workOrderId?: number;
  workOrderNumber?: string;
  parserUsed?: string;
  confidence?: string;
  errorMessage?: string;
  preview?: WorkOrderEmailImportPreview;
}

export interface InboundEmailPendingTest {
  hasReceivedEmail: boolean;
  latestEmail?: InboundEmailLogEntry;
}

export interface PagedInboundEmailLogs {
  data: InboundEmailLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface StepCompletionProps {
  data: WorkOrderWizardData;
  onChange: (data: Partial<WorkOrderWizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSubmitting: boolean;
  onWorkOrderCreated?: () => void; // New prop to notify when work order is created
}

// Audit Log Types
export interface AuditLog {
  id: number;
  userId: string;
  userName: string;
  companyId: number;
  companyName?: string;
  entityType: string;
  entityId?: number;
  action: string;
  oldValues?: string;
  newValues?: string;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
  details?: string;
  
  // New error logging fields
  logLevel?: string;
  errorCode?: string;
  exceptionDetails?: string;
  requestPath?: string;
  httpMethod?: string;
  httpStatusCode?: number;
}

export interface AuditLogFilters {
  searchTerm?: string;
  userId?: string;
  companyId?: number;
  action?: string;
  entityType?: string;
  startDate?: string;
  endDate?: string;
  pageNumber: number;
  pageSize: number;

  // New error filtering fields
  logLevel?: string;
  errorCode?: string;
}

export interface AuditLogResponse {
  data: AuditLog[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AuditLogExport {
  timestamp: string;
  userName: string;
  action: string;
  entityType: string;
  entityId?: number;
  ipAddress?: string;
  details?: string;
}

// Work Order Template Types
export interface WorkOrderTemplate {
  id: number;
  companyId: number;
  name: string;
  htmlContent: string;
  isActive: boolean;
  isDefault: boolean;
  createdDate: string;
  updatedDate: string;
  usageCount: number;
}

export interface CreateWorkOrderTemplate {
  name: string;
  htmlContent: string;
  isActive: boolean;
  isDefault: boolean;
}

export interface UpdateWorkOrderTemplate {
  name: string;
  htmlContent: string;
  isActive: boolean;
  isDefault: boolean;
}

export interface TemplatePreview {
  renderedHtml: string;
  templateName: string;
  generatedDate: string;
}

export interface TemplatePlaceholder {
  placeholder: string;
  description: string;
  category: string;
}

export interface TemplateUsage {
  templateId: number;
  templateName: string;
  usageCount: number;
  lastUsed: string;
}

// Contact Types
export interface ClientContact {
  id: number;
  clientId: number;
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary: boolean;
  isActive: boolean;
  notes?: string;
  createdDate: string;
  updatedDate?: string;
  fullName: string;
  displayName: string;
}

export interface CreateClientContactForm {
  clientId: number;
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary: boolean;
  notes?: string;
}

export interface UpdateClientContactForm {
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary: boolean;
  notes?: string;
}

export interface VendorContact {
  id: number;
  vendorId: number;
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary: boolean;
  isActive: boolean;
  notes?: string;
  createdDate: string;
  updatedDate?: string;
  fullName: string;
  displayName: string;
}

export interface CreateVendorContactForm {
  vendorId: number;
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary: boolean;
  notes?: string;
}

export interface UpdateVendorContactForm {
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary: boolean;
  notes?: string;
}

// Bulk Edit Types
export interface BulkUpdateWorkOrderDto {
  workOrderIds: number[];
  updates: BulkUpdateFields;
}

export interface BulkUpdateFields {
  statusId?: number;
  assignedUserId?: string;
  priorityId?: number;
}

export interface BulkUpdateResultDto {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  errors: BulkUpdateErrorDto[];
}

export interface BulkUpdateErrorDto {
  workOrderId: number;
  workOrderNumber: string;
  errorMessage: string;
}

export interface BulkEditFormData {
  updateStatus: boolean;
  statusId?: number;
  updateAssignedUser: boolean;
  assignedUserId?: string;
  updatePriority: boolean;
  priorityId?: number;
}

// User Dashboard Types
export interface UserDashboardStatsDto {
  myOpenWorkOrders: number;
  workOrdersDueToday: number;
  overdueWorkOrders: number;
  workOrdersCompletedThisMonth: number;
  averageDaysToComplete: number;
  totalWorkOrdersAssigned: number;
  workOrdersInProgress: number;
  workOrdersAssigned: number;
  workOrdersCompleted: number;
  workOrdersCancelled: number;
  performanceMetrics: UserPerformanceMetricsDto;
}

export interface UserPerformanceMetricsDto {
  completedThisMonth: number;
  completedLastMonth: number;
  completionTrend: number; // Percentage change from last month
  averageCompletionTime: number; // In days
  onTimeCompletions: number;
  totalCompletions: number;
  onTimeCompletionRate: number; // Percentage
}

export interface DispatcherDashboardStatsDto {
  myAssignedOpen: number;
  myOverdue: number;
  unassignedCount: number;
  pendingClientApprovalCount: number;
  nteExceededCount: number;
  documentationGapsCount: number;
  readyToInvoiceCount: number;
}

export interface DispatcherQueueItemDto {
  queueType: string;
  workOrderId: number;
  estimateId?: number;
  workOrderNumber: string;
  clientName: string;
  statusName: string;
  priorityName?: string;
  categoryName?: string;
  description?: string;
  externalWorkOrderNumber?: string;
  assignedUserName?: string;
  vendorName?: string;
  vendorAddress?: string;
  vendorCity?: string;
  vendorState?: string;
  vendorZipCode?: string;
  vendorSearchText?: string;
  locationName?: string;
  locationAddress?: string;
  locationCity?: string;
  locationState?: string;
  locationZipCode?: string;
  nteAmount?: number;
  estimateTotal?: number;
  clientApprovalStatus?: string;
  summary?: string;
  dueDate?: string;
  isOverdue: boolean;
  updatedDate?: string;
}

export interface DispatcherQueueFiltersDto {
  queueType?: string;
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
}

export interface UserWorkOrderSummaryDto {
  id: number;
  workOrderNumber: string;
  clientName: string;
  clientLocationId?: number;
  clientLocationName?: string;
  clientLocationContactName?: string;
  clientLocationContactPhone?: string;
  clientLocationContactEmail?: string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientZipCode?: string;
  clientPhone?: string;
  clientEmail?: string;
  statusId: number;
  statusName: string;
  priorityId: number;
  priorityName: string;
  priorityColorCode: string;
  categoryId?: number;
  categoryName?: string;
  dueDate?: string;
  daysRemaining: number;
  isOverdue: boolean;
  createdDate: string;
  completedDate?: string;
  primaryVendorName?: string;
  primaryVendorAddress?: string;
  primaryVendorCity?: string;
  primaryVendorState?: string;
  primaryVendorZipCode?: string;
  primaryVendorPhone?: string;
  primaryVendorEmail?: string;
  vendorCount: number;
  assignedUserId: string;
  assignedUserName: string;
  description?: string;
}

export interface UserDashboardFiltersDto {
  searchTerm?: string;
  statusFilter?: string; // "all", "due-today", "overdue", or status name
  priorityFilter?: string; // "all" or priority name
  page?: number;
  pageSize?: number;
  sortBy?: string; // "dueDate", "priority", "status", "createdDate"
  sortDirection?: string; // "asc" or "desc"
}

export interface RecentActivityDto {
  id: number;
  activityType: string; // "Assigned", "StatusChanged", "Completed", "CommentAdded"
  description: string;
  activityDate: string;
  workOrderId?: number;
  workOrderNumber?: string;
  clientName?: string;
  oldValue?: string;
  newValue?: string;
}

export interface UserDashboardDto {
  stats: UserDashboardStatsDto;
  workOrders: PagedResponseDto<UserWorkOrderSummaryDto>;
  recentActivity: RecentActivityDto[];
  userName: string;
  lastUpdated: string;
}

// Admin Dashboard Types
export interface AdminSystemStatsDto {
  totalWorkOrders: number;
  activeUsers: number;
  openWorkOrders: number;
  overdueWorkOrders: number;
  overduePercentage: number;
  averageResolutionTimeHours: number;
  totalCompanies: number;
  activeCompanies: number;
  totalVendors: number;
  activeVendors: number;
  workOrdersThisMonth: number;
  workOrdersLastMonth: number;
  monthlyGrowthPercentage: number;
  usersThisMonth: number;
  usersLastMonth: number;
  userGrowthPercentage: number;
}

export interface ChartDataPointDto {
  label: string;
  value: number;
  color?: string;
}

export interface TimeSeriesDataPointDto {
  date: string;
  value: number;
  label?: string;
}

export interface VendorPerformanceDto {
  vendorId: number;
  vendorName: string;
  averageRating: number;
  completedWorkOrders: number;
  totalWorkOrders: number;
  completionRate: number;
}

export interface UserActivityDto {
  userId: string;
  userName: string;
  companyName: string;
  workOrdersCreated: number;
  workOrdersCompleted: number;
  lastLoginDate: string;
  loginCountThisMonth: number;
}

export interface AdminChartsDataDto {
  workOrdersByStatus: ChartDataPointDto[];
  workOrdersTrend: TimeSeriesDataPointDto[];
  workOrdersByPriority: ChartDataPointDto[];
  workOrdersByCategory: ChartDataPointDto[];
  companyActivity: ChartDataPointDto[];
  userAgentsByDevice: ChartDataPointDto[];
  topVendors: VendorPerformanceDto[];
  topUsers: UserActivityDto[];
}

export interface SystemHealthDto {
  databaseResponseTimeMs: number;
  activeSessions: number;
  systemUptime: string; // TimeSpan as string
  errorRate24Hours: number;
  storageUsageBytes: number;
  failedLoginAttempts24Hours: number;
  lastBackupDate: string;
  isSystemHealthy: boolean;
  healthWarnings: string[];
}

export interface RecentSystemActivityDto {
  id: number;
  activityType: string;
  description: string;
  userName: string;
  companyName: string;
  timestamp: string;
  severity: string; // Info, Warning, Error, Critical
}

// Company Activity & Usage Statistics
export interface CompanyActivityStats {
  lastLoginDate?: string | null;
  lastActivityDate?: string | null;
  activeUsersLast30Days: number;
  workOrdersCreatedLast30Days: number;
  invoicesCreatedLast30Days: number;
  healthStatus: 'Healthy' | 'At Risk' | 'Inactive';
}

export interface CompanyUsageStats {
  totalWorkOrders: number;
  workOrdersLast30Days: number;
  totalInvoices: number;
  invoicesLast30Days: number;
  totalClients: number;
  totalVendors: number;
  activeUsers: number;
  storageUsedBytes: number;
  storageUsedFormatted: string;
  oldestWorkOrderDate?: string | null;
  mostRecentWorkOrderDate?: string | null;
}

// Company Notes
export interface CompanyNote {
  id: number;
  companyId: number;
  note: string;
  noteType: 'General' | 'Support' | 'Billing' | 'Technical';
  createdBy: string;
  createdByName: string;
  createdDate: string;
  isInternal: boolean;
}

export interface CreateCompanyNote {
  note: string;
  noteType: 'General' | 'Support' | 'Billing' | 'Technical';
  isInternal?: boolean;
}

// Email Company Admin
export interface SendCompanyEmail {
  subject: string;
  body: string;
  templateId?: string;
  additionalRecipients?: string[];
}

export interface CompanyMetricsDto {
  companyId: number;
  companyName: string;
  totalWorkOrders: number;
  activeWorkOrders: number;
  totalUsers: number;
  activeUsers: number;
  totalVendors: number;
  averageResolutionTimeHours: number;
  createdDate: string;
  lastActivityDate: string;
  isActive: boolean;
}

export interface AdminDashboardFiltersDto {
  startDate?: string;
  endDate?: string;
  companyId?: number;
  dateRange?: string; // "7d", "30d", "90d", "1y", "all"
}

// Invoice Types
export interface InvoiceDto {
  id: number;
  companyId: number;
  workOrderId?: number;
  invoiceNumber: string;
  
  // Client and Location references
  clientId?: number;
  clientLocationId?: number;
  
  // Flattened client data for display (populated by AutoMapper)
  clientName: string;
  clientLocationName?: string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientZipCode?: string;
  clientPhone?: string;
  clientEmail?: string;

  // Flattened company data for display (populated by AutoMapper)
  companyName?: string;
  companyAddress?: string;
  companyCity?: string;
  companyState?: string;
  companyZipCode?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyLogoUrl?: string;
  companyLogoBase64?: string;

  invoiceDate: string;
  dueDate: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  
  // Discount fields
  discountAmount: number;
  discountType: string;
  discountPercentage?: number;
  discountDescription?: string;
  
  total: number;
  currencyCode?: string;
  notes?: string;
  terms?: string;
  paymentInfo?: string;
  status: InvoiceStatus;
  createdAt: string;
  updatedAt?: string;
  sentAt?: string;
  paidAt?: string;
  items: InvoiceItemDto[];
  workOrder?: WorkOrderDto;
  company?: Company;
  templateId?: number;
  templateName?: string;
}

export interface InvoiceItemDto {
  id: number;
  invoiceId: number;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  sortOrder: number;
  createdAt: string;
  
  // Link back to timeline item for billing tracking
  jobContactEntryId?: number;
}

export enum InvoiceStatus {
  Draft = 'Draft',
  Sent = 'Sent',
  Paid = 'Paid',
  Partial = 'Partial',
  Overdue = 'Overdue',
  Cancelled = 'Cancelled',
  Archived = 'Archived'
}

export enum InvoiceType {
  Custom = 0,      // Manual invoice creation
  Progress = 1,    // Progress billing from work order
  Final = 2,       // Final billing from work order
  Timeline = 3     // Invoice from specific timeline items
}

export interface CreateInvoiceDto {
  companyId: number;
  
  // Invoice Type and Source
  type: InvoiceType;
  workOrderId?: number;
  description?: string; // For progress invoices
  
  // Timeline Integration
  selectedTimelineEntryIds?: number[];
  
  invoiceNumber: string;
  // Client and Location references (proper normalized design)
  clientId?: number;
  clientLocationId?: number;
  
  // Flattened client data for display (populated by AutoMapper)
  clientName: string;
  clientLocationName?: string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientZipCode?: string;
  clientPhone?: string;
  clientEmail?: string;
  invoiceDate: string;
  dueDate: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  
  // Discount fields
  discountAmount: number;
  discountType: string;
  discountPercentage?: number;
  discountDescription?: string;
  
  total: number;
  currencyCode?: string;
  notes?: string;
  terms?: string;
  paymentInfo?: string;
  status: InvoiceStatus;
  
  // Items (can be auto-populated or manually specified)
  customItems?: CreateInvoiceItemDto[];
}

export interface CreateInvoiceItemDto {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  sortOrder: number;
  
  // Link back to timeline item for billing tracking
  jobContactEntryId?: number;
}

export interface UpdateInvoiceDto {
  id: number;
  invoiceDate?: string;
  dueDate?: string;
  subtotal?: number;
  taxRate?: number;
  taxAmount?: number;
  
  // Discount fields
  discountAmount?: number;
  discountType?: string;
  discountPercentage?: number;
  discountDescription?: string;
  
  total?: number;
  currencyCode?: string;
  notes?: string;
  terms?: string;
  paymentInfo?: string;
  status?: InvoiceStatus;
  items?: UpdateInvoiceItemDto[];
}

export interface UpdateInvoiceItemDto {
  id?: number;
  description?: string;
  quantity?: number;
  rate?: number;
  amount?: number;
  sortOrder?: number;
  
  // Link back to timeline item for billing tracking
  jobContactEntryId?: number;
}

export interface InvoiceStatistics {
  totalCount: number;
  totalAmount: number;
  paidCount: number;
  paidAmount: number;
  pendingCount: number;
  pendingAmount: number;
  overdueCount: number;
  overdueAmount: number;
  draftCount: number;
  draftAmount: number;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  dateRangeLabel?: string;
}

export interface InvoicePaymentSummary {
  workOrderId: number;
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  invoiceCount: number;
  paidInvoiceCount: number;
  paymentPercentage: number;
}

// Credit Memo Types
export interface CreditMemoDto {
  id: number;
  companyId: number;
  creditMemoNumber: string;
  originalInvoiceId: number;
  originalInvoiceNumber: string;
  reason?: string;
  amount: number;
  taxAmount: number;
  total: number;
  status: string;
  createdAt: string;
  updatedAt?: string;
  appliedAt?: string;
  createdBy?: string;
  appliedBy?: string;
  notes?: string;
  items: CreditMemoItemDto[];
}

export interface CreditMemoItemDto {
  id: number;
  creditMemoId: number;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  isTaxable: boolean;
  unit?: string;
  notes?: string;
}

export interface CreateCreditMemoDto {
  originalInvoiceId: number;
  reason?: string;
  notes?: string;
  items: CreateCreditMemoItemDto[];
}

export interface CreateCreditMemoItemDto {
  description: string;
  quantity: number;
  rate: number;
  isTaxable: boolean;
  unit?: string;
  notes?: string;
}

export interface UpdateCreditMemoDto {
  reason?: string;
  notes?: string;
  items?: UpdateCreditMemoItemDto[];
}

export interface UpdateCreditMemoItemDto {
  id?: number;
  description?: string;
  quantity?: number;
  rate?: number;
  isTaxable?: boolean;
  unit?: string;
  notes?: string;
}

// Payment Types
export interface PaymentDto {
  id: number;
  companyId: number;
  invoiceId: number;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  referenceNumber?: string;
  notes?: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt?: string;
  updatedBy?: string;
  updatedByName?: string;
}

export interface CreatePaymentDto {
  invoiceId: number;
  amount: number;
  paymentMethod: string;
  referenceNumber?: string;
  notes?: string;
  paymentDate?: string;
}

export interface UpdatePaymentDto {
  id: number;
  amount: number;
  paymentMethod: string;
  referenceNumber?: string;
  notes?: string;
}

// Timeline Entry Metadata Types
export interface IncludeOnInvoiceMetadata {
  quantity: number;
  rate: number;
  isTaxable: boolean;
  description?: string;
  unit?: string;
}

export interface CallMetadata {
  phoneNumber?: string;
  durationMinutes: number;
  callDirection: string; // "Inbound" or "Outbound"
  contactPerson?: string;
  callType?: string; // "General", "Emergency", "Follow-up", etc.
}

export interface StatusUpdateMetadata {
  previousStatus?: string;
  newStatus: string;
  reason?: string;
  updatedBy?: string;
}

export interface VendorMessageMetadata {
  vendorId: number;
  vendorName?: string;
  messageType?: string; // "General", "Quote", "Update", "Issue"
  priority?: string; // "Low", "Normal", "High", "Urgent"
  contactPerson?: string;
  responseRequired: boolean;
}

export interface ClientMessageMetadata {
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  responseRequired: boolean;
  messageType?: string; // "General", "Question", "Complaint", "Praise"
  priority?: string; // "Low", "Normal", "High", "Urgent"
}

export interface FileUploadMetadata {
  fileName: string;
  fileType: string; // "image", "document", "spreadsheet", etc.
  fileSizeBytes: number;
  description?: string;
  isConfidential: boolean;
}

export interface EmailMetadata {
  toEmail?: string;
  fromEmail?: string;
  subject?: string;
  emailType?: string; // "General", "Quote", "Invoice", "Update"
  isOutbound: boolean;
  hasAttachments: boolean;
}

export interface SystemMessageMetadata {
  messageType: string; // "AutoStatusChange", "Reminder", "Alert"
  triggeredBy?: string;
  additionalData?: string;
  isUserVisible: boolean;
}

export interface VerbalAuthorizationMetadata {
  vendorId: number;
  vendorName?: string;
  amountAuthorized: number;
  authorizedByName?: string;
  authorizationMethod?: string;
  notes?: string;
}

export type EstimateStatus = 'Draft' | 'Submitted' | 'Approved' | 'Rejected' | 'Converted';

export interface EstimateItem {
  id?: number;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  sortOrder: number;
  isTaxable: boolean;
}

export interface EstimateItemInput {
  description: string;
  quantity: number;
  rate: number;
  sortOrder: number;
  isTaxable: boolean;
}

export interface NteComparison {
  nteAmount?: number;
  estimateTotal: number;
  exceedsNte: boolean;
  amountOverNte?: number;
  suggestedStatusName?: string;
  alertMessage?: string;
}

export interface Estimate {
  id: number;
  workOrderId: number;
  vendorId: number;
  vendorName?: string;
  status: number;
  statusLabel: string;
  notes?: string;
  documentUrl?: string;
  totalAmount: number;
  exceedsNte: boolean;
  nteAmountSnapshot?: number;
  amountOverNte?: number;
  submittedDate?: string;
  approvedDate?: string;
  rejectedDate?: string;
  rejectedReason?: string;
  convertedInvoiceId?: number;
  convertedInvoiceNumber?: string;
  clientApprovalStatus: ClientApprovalStatus;
  clientApprovalStatusLabel: string;
  clientApprovalRequestedAt?: string;
  clientApprovalRespondedAt?: string;
  clientApprovedByName?: string;
  clientRejectionReason?: string;
  clientApprovalEmail?: string;
  nteComparison?: NteComparison;
  items: EstimateItem[];
  createdDate: string;
  createdBy?: string;
}

export enum ClientApprovalStatus {
  None = 0,
  Pending = 1,
  Approved = 2,
  Rejected = 3,
}

export interface EstimatePublicApproval {
  workOrderNumber: string;
  workOrderTitle?: string;
  clientName: string;
  locationName?: string;
  vendorName: string;
  totalAmount: number;
  nteAmount?: number;
  amountOverNte?: number;
  companyName: string;
  clientApprovalStatus: ClientApprovalStatus;
  clientApprovalStatusLabel: string;
  isExpired: boolean;
  isAlreadyResponded: boolean;
  items: EstimateItem[];
}

export interface RequestClientApprovalForm {
  recipientEmail?: string;
  customMessage?: string;
}

export interface VendorCoiPublicPolicy {
  policyId: number;
  policyType: number;
  policyTypeLabel: string;
  carrierName?: string;
  policyNumber?: string;
  expirationDate?: string;
  hasDocument: boolean;
  isVerified: boolean;
  policyStatus: string;
}

export interface VendorCoiPublicUpload {
  companyName: string;
  vendorName: string;
  expiresAt: string;
  isExpired: boolean;
  requirePolicyVerification: boolean;
  policies: VendorCoiPublicPolicy[];
}

export interface VendorCoiPublicUploadResult {
  policyId: number;
  policyTypeLabel: string;
  pendingVerification: boolean;
  message: string;
}

export interface SendVendorComplianceUploadLinkForm {
  recipientEmail?: string;
  customMessage?: string;
}

export interface CreateEstimateForm {
  workOrderId: number;
  vendorId: number;
  notes?: string;
  items: EstimateItemInput[];
}

export interface UpdateEstimateForm {
  notes?: string;
  items: EstimateItemInput[];
}

// Invoice Health Check Types
export interface InvoiceHealthCheckResult {
  overdueCount: number;
  upcomingDueCount: number;
  notificationsCreated: number;
  issues: InvoiceHealthIssue[];
}

export interface InvoiceHealthIssue {
  invoiceId: number;
  invoiceNumber: string;
  issueType: string; // "Overdue" or "DueSoon"
  daysOverdue: number;
  daysUntilDue: number;
  amountDue: number;
  clientName: string;
}

// Bulk Operations Types
export interface BulkMarkAsSentDto {
  invoiceIds: number[];
  sendEmail: boolean;
}

export interface BulkArchiveDto {
  invoiceIds: number[];
}

export interface BulkOperationResult {
  successCount: number;
  failedCount: number;
  errors: string[];
}

export interface InvoiceExportFilter {
  status?: string;
  startDate?: string;
  endDate?: string;
  invoiceIds?: number[];
}

// Aging Report Types
export interface AgingReportDto {
  asOfDate: string;
  summary: AgingReportSummaryDto;
  byClient: AgingReportClientDto[];
}

export interface AgingReportSummaryDto {
  current: number;
  days1To30: number;
  days31To60: number;
  days61To90: number;
  days90Plus: number;
  totalOutstanding: number;
}

export interface AgingReportClientDto {
  clientId: number;
  clientName: string;
  current: number;
  days1To30: number;
  days31To60: number;
  days61To90: number;
  days90Plus: number;
  totalDue: number;
  invoices: AgingReportInvoiceDto[];
}

export interface AgingReportInvoiceDto {
  invoiceId: number;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  amountDue: number;
  daysOverdue: number;
  ageBucket: string;
}
